import React, { useEffect, useContext, useRef } from "react";

// Material UI
import Typography from "@material-ui/core/Typography";
import { useTheme } from "@material-ui/core";

// Contexts
import { AppContext } from "AppProvider";
import { useUiContext } from "context/UiContext";
import { useMap } from "context/Map2dContext";

// Hooks
import useUnitSystem from "hooks/useUnitSystem";

// Components
import { ScaleLine, MousePosition } from "ol/control.js";
import { createStringXY } from "ol/coordinate.js";

// i18n
import { useTranslation } from "react-i18next";

// Utils
import clsx from "clsx";

// Styles
import useStyles from "./styles";

// Constants
import { EPSG_4326 } from "assets/global";

/**
 * Footer component for the 2D dashboard.
 * Displays the coordinates and scale bar.
 *
 * @returns {JSX.Element} The Footer component
 */
const OpenLayersFooter = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();

  const [uiState] = useUiContext();
  const mapContext = useMap();
  const appContext = useContext(AppContext);
  const { system } = useUnitSystem();

  const scaleLineRef = useRef(null);
  const mousePositionRef = useRef(null);
  const systemRef = useRef(system);
  systemRef.current = system;

  /**
   * Mount ScaleLine and MousePosition once when the map is available.
   */
  useEffect(() => {
    const olmap = mapContext.olmap;
    if (!olmap) return;

    const scaleLineControl = new ScaleLine({
      units: systemRef.current,
      target: "scale-bar"
    });
    olmap.addControl(scaleLineControl);
    scaleLineRef.current = scaleLineControl;

    const mousePosition = new MousePosition({
      coordinateFormat: createStringXY(4),
      projection: EPSG_4326,
      className: "custom-mouse-position",
      placeholder: "&#160;",
      target: "mouse-position"
    });
    olmap.addControl(mousePosition);
    mousePositionRef.current = mousePosition;

    return () => {
      olmap.removeControl(scaleLineControl);
      olmap.removeControl(mousePosition);
      scaleLineRef.current = null;
      mousePositionRef.current = null;
    };
  }, [mapContext.olmap]);

  /**
   * Sync scale bar units when the user toggles metric/imperial.
   */
  useEffect(() => {
    scaleLineRef.current?.setUnits?.(system);
  }, [system]);

  return (
    <div
      id="footer2d"
      style={{
        position: "absolute",
        bottom: uiState.bottomDrawerOpen ? uiState.bottomDrawerHeight : 0,
        left: 0,
        color: "#eee",
        fontSize: "0.75em",
        maxHeight: "28px",
        width: "100%",
        paddingLeft:
          uiState.leftDrawerWidth === 0 ? 10 : uiState.leftDrawerWidth,
        paddingRight:
          uiState.rightDrawerWidth === 0 ? 10 : uiState.rightDrawerWidth + 20,
        transition: "bottom 0.2s linear"
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-start",
          background: "rgba(0, 0, 0,0.5)"
        }}
      >
        <Typography
          variant="body2"
          style={{
            paddingRight: theme.spacing(1),
            paddingLeft: theme.spacing(1)
          }}
        >
          {t("common.Coordinates")}:{" "}
        </Typography>
        <Typography
          style={{ minWidth: "150px" }}
          variant="body2"
          id="mouse-position"
        ></Typography>

        <div
          id="scale-bar"
          style={{
            position: "relative",
            height: "18px"
          }}
          className={clsx(
            appContext.state.rightOpen
              ? classes.scaleLinePad
              : classes.scaleLine,
            classes.scale
          )}
        />
      </div>
    </div>
  );
};

export default OpenLayersFooter;
